<template>
  <BaseModalLarge
    :heading="$t(`register.${orderKey()}.details`)"
    :loading="loading"
    :label="order ? order.state : null"
    :actions="actions"
    :parentRoute="{
      name: parentRouteName
    }"
    @action="onActionClick"
    testId="sale"
  >
    <template #aside>
      <Customer
        :orderId="route.params.id"
        :customer="order?.customer"
        @updated="order.customer = $event"
      />
    </template>
    <Flex
      v-if="order"
      direction="column"
      :gap="1.5"
    >
      <Details :order="order" />
      <Summary :order="order" />
      <Documents
        v-if="hasFeatureFlag('compliant-register') && !order.draft"
        ref="documentsRef"
        :documents="documentsIncludingLegacy"
        :order="order"
      />
      <Appointments
        v-if="order?.appointments?.length"
        :appointments="order.appointments"
      />
      <Note
        v-if="order.comments"
        :note="order.comments"
      />
      <TimelineItems />
    </Flex>
    <PrintUrl
      v-if="promptPrint"
      :url="printUrl"
    />
    <CreateNoteModal
      v-if="showCreateNoteModal"
      :currentNote="order?.comments"
      @close="showCreateNoteModal = false"
      @submit="setNote"
    />

    <IssueInvoice
      v-if="showIssueInvoiceModal"
      :orderId="order.id"
      @submit="handleIssueInvoiceSubmit"
      @close="showIssueInvoiceModal = false"
    />

    <SendPaymentLink
      v-if="showSendPaymentLinkModal"
      :orderId="order.id"
      :email="order?.customer?.email"
      @close="showSendPaymentLinkModal = false"
    />
    <RefundSaleModal
      v-if="showRefundSaleModal"
      :orderId="order.id"
      @submit="handleRefundSaleSubmit"
      @close="showRefundSaleModal = false"
    />
  </BaseModalLarge>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PrintUrl from '@/components/PrintUrl.vue';
import config from '@/config';
import filters from '@/filters';
import { compliantKey, orderKey } from '@/helpers/compliant-register';
import { scrollPage } from '@/helpers/scroll';
import { flash, modal } from '@/helpers/ui';
import CreateNoteModal from '@/modals/CreateNote.vue';
import SendPaymentLink from '@/modals/SendPaymentLink.vue';
import IssueInvoice from '@/modules/register/modals/issue-invoice/index.vue';
import RefundSaleModal from '@/modules/register/modals/refund-sale/index.vue';
import { useRegisterStore } from '@/modules/register/stores/register';
import { segment } from '@/segment';
import { useCompanyStore } from '@/stores/company';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import { useUserStore } from '@/stores/user';
import { type Order, RegisterDocumentEnum } from '@/types';
import unleash from '@/unleash';
import Appointments from './Appointments.vue';
import Customer from './Customer.vue';
import Details from './Details.vue';
import Documents from './Documents.vue';
import { DELETE_ORDER, GET_ORDER, UPDATE_ORDER } from './graphql';
import Note from './Note.vue';
import Summary from './summary/index.vue';
import TimelineItems from './TimelineItems.vue';

const { t } = useI18n();

const props = defineProps<{
  parentRouteName: string;
}>();

const route = useRoute();
const router = useRouter();
const promptPrint = ref(false);
const showCreateNoteModal = ref(false);
const order = ref<Order | null>(null);
const showIssueInvoiceModal = ref(false);
const showSendPaymentLinkModal = ref(false);
const showRefundSaleModal = ref(false);
const documentsRef = ref();

const { companySettings } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();
const drawerStore = useRegisterDrawerStore();
const { currentCashupId, isLoaded: isDrawerLoaded } = storeToRefs(drawerStore);
const { cashupEnabled, isEditing, isPaying } = storeToRefs(useRegisterStore());

// The drawer status is only fetched by the register module, so it is missing
// when this modal opens from a customer page.
if (cashupEnabled.value && !isDrawerLoaded.value) {
  drawerStore.getDrawerStatus();
}

const { loading, onResult, refetch } = useQuery(GET_ORDER, {
  id: route.params.id,
});

onResult(({ data: { order: orderData } }: any) => {
  order.value = orderData;
});

const { mutate: updateOrderMutation } = useMutation(UPDATE_ORDER);
const { mutate: deleteOrderMutation } = useMutation(DELETE_ORDER, {
  variables: {
    input: {
      id: route.params.id,
    },
  },
  update(cache) {
    cache.evict({ id: 'ROOT_QUERY', fieldName: 'orders' });
    cache.gc();
  },
});

const needsFiscalizationData = computed(() => {
  return (
    companySettings.fiscalization?.tssId && !order.value?.fiscalizationFinished
  );
});

const showPrintReceipt = computed(() => !needsFiscalizationData.value);

const printUrl = computed(
  () => `${config.backendUrl}/orders/${order.value?.id}`,
);

const hasTerminalTransaction = computed(() => {
  return (
    unleash.isEnabled('SalonizedPay') &&
    !!order.value?.transactions?.find(
      (transaction) => transaction.type === 'POS',
    )
  );
});

const handleRefundSaleSubmit = () => {
  refetch();
  showRefundSaleModal.value = false;
};

const handleIssueInvoiceSubmit = () => {
  refetch();
  showIssueInvoiceModal.value = false;
};

const hasInvoiceDocument = computed(
  () =>
    !!order.value?.documents?.find(
      (document) => document.documentType === RegisterDocumentEnum.Invoice,
    ),
);

const hasDocuments = computed(
  () =>
    hasFeatureFlag('compliant-register') &&
    !!documentsIncludingLegacy.value?.length,
);

const documentsIncludingLegacy = computed(() => {
  const documents = [...(order.value?.documents || [])];
  if (hasLegacyInvoice.value) {
    documents.push({
      id: order.value?.id,
      documentType: RegisterDocumentEnum.Invoice,
      number: order.value?.number,
      totalAmount: order.value?.total,
      totalAmountInclTaxExempt: order.value?.total,
      issuedAt: order.value?.invoicedAt,
      referencedDocuments: [],
      legacy: true,
    });
  }
  return documents;
});

const hasLegacyInvoice = computed(() => {
  return (
    !order.value?.draft &&
    order.value?.invoicedAt &&
    !order.value?.compliantRegister
  );
});

const blockFiskalyActions = computed(
  () =>
    unleash.isEnabled('EnableFiskalySign') &&
    hasFeatureFlag('compliant-register') &&
    hasFeatureFlag('fiskaly-sign') &&
    !hasFeatureFlag('fiskaly-sign-enabled'),
);

const complianceActions = computed(() => {
  return [
    {
      name: 'view-documents',
      icon: 'eye',
      label: filters.capitalize(t('global.actions.view_invoices_and_receipts')),
      hide: !hasDocuments.value || order.value?.draft,
    },
    {
      name: 'issue-invoice',
      icon: 'file',
      label: filters.capitalize(t('global.actions.issue_invoice')),
      hide:
        !order.value?.permissions.issueInvoice ||
        hasInvoiceDocument.value ||
        blockFiskalyActions.value,
      component: 'issue-invoice',
    },
    {
      name: 'send-payment-link',
      icon: 'paper-plane',
      label: filters.capitalize(t('global.actions.send_payment_link')),
      component: 'send-payment-link',
      hide:
        !companySettings.sales.paymentLink ||
        order.value?.draft ||
        order.value?.state === 'VOIDED' ||
        !order.value?.amountOutstanding ||
        (order.value?.amountOutstanding &&
          order.value?.amountOutstanding < 0) ||
        blockFiskalyActions.value,
    },
    {
      name: 'note',
      icon: 'message-circle',
      label: filters.capitalize(
        t(
          order.value?.comments
            ? 'global.actions.edit_note'
            : 'global.actions.add_note',
        ),
      ),
    },
    {
      name: 'refund',
      icon: 'refresh',
      component: 'refund-sale',
      label: filters.capitalize(t('global.actions.refund_sale')),
      hide: !order.value?.permissions.credit || blockFiskalyActions.value,
    },
  ];
});

const legacyActions = computed(() => {
  return [
    {
      name: 'invoice',
      icon: 'pdf',
      label: filters.capitalize(t('global.actions.view_item', { item: 'PDF' })),
      href: `${config.backendUrl}/orders/${order.value?.id}.pdf`,
      hide: needsFiscalizationData.value,
    },
    {
      name: 'email',
      icon: 'email',
      label: filters.capitalize(
        t('global.actions.email_item', {
          item: t('global.items.invoice', 1),
        }),
      ),
      routerLink: route.params.customerId
        ? {
            name: 'customer-sale-email',
            params: {
              id: order.value?.id,
              customerId: route.params.customerId,
            },
          }
        : {
            name: 'invoice-email',
            params: {
              id: order.value?.id,
            },
          },
      hide: needsFiscalizationData.value,
    },
    {
      name: 'print',
      icon: 'printer',
      label: filters.capitalize(t('global.actions.print_receipt')),
      hide: !showPrintReceipt.value,
    },
    {
      name: 'note',
      icon: 'message-circle',
      label: filters.capitalize(
        t(
          order.value?.comments
            ? 'global.actions.edit_note'
            : 'global.actions.add_note',
        ),
      ),
    },
    {
      name: 'credit',
      icon: 'copy',
      label: filters.capitalize(t('global.actions.create_credit_invoice')),
      hide: !order.value?.permissions.credit,
    },
  ];
});

const disableCancelToolTip = computed(() => {
  if (hasFeatureFlag('compliant-register')) {
    if (
      order.value?.cashupId &&
      order.value?.cashupId !== Number(currentCashupId.value)
    ) {
      return t('register.payment_undeletable_tooltip');
    }
    return t('register.sale.cancel_sale_tooltip');
  }

  return t('register.pos.delete_tooltip');
});

const baseActions = computed(() => {
  return [
    {
      name: 'pay',
      icon: 'shopping-cart',
      label: filters.capitalize(
        t('global.actions.pay_item', {
          item: t(`global.items.${orderKey()}`, 1),
        }),
      ),
      component: 'pay-sale',
      hide:
        !order.value?.permissions.edit ||
        order.value?.state !== 'OUTSTANDING' ||
        blockFiskalyActions.value,
    },
    {
      name: 'edit',
      icon: 'edit',
      label: filters.capitalize(
        t('global.actions.edit_item', {
          item: t(`global.items.${orderKey()}`, 1),
        }),
      ),
      component: 'edit-sale',
      hide:
        !order.value?.permissions.editOrderItems || blockFiskalyActions.value,
    },
    {
      name: 'delete',
      icon: 'delete',
      label: filters.capitalize(
        t(compliantKey('global.actions.delete_item'), {
          item: t(`global.items.${orderKey()}`, 1),
        }),
      ),
      component: 'void-sale',
      hide:
        (!hasFeatureFlag('compliant-register') &&
          !order.value?.permissions.delete) ||
        blockFiskalyActions.value,
      disabled:
        !order.value?.permissions.delete || hasTerminalTransaction.value,
      disabledTooltip: disableCancelToolTip.value,
    },
  ];
});

const actions = computed(() => {
  if (!order.value) return [];

  const list = [];

  if (hasFeatureFlag('compliant-register') && complianceActions.value.length) {
    list.push(...complianceActions.value);
  } else if (
    !hasFeatureFlag('compliant-register') &&
    legacyActions.value.length
  ) {
    list.push(...legacyActions.value);
  }

  list.push(...baseActions.value);

  return list.filter((item) => !item.hide);
});

const setCurrentState = (state: string) => {
  if (state === 'edit') {
    isEditing.value = true;
  }
  if (state === 'pay') {
    isPaying.value = true;
  }
};

const onActionClick = (action: string) => {
  if (['credit', 'pay', 'edit'].includes(action)) {
    setCurrentState(action);
    router.push({
      name: 'register',
      query: { orderId: order.value?.id, action },
    });
  } else {
    switch (action) {
      case 'issue-invoice':
        segment.track('register.sales.issue_invoice.clicked', {
          origin: 'sale_overview',
        });
        showIssueInvoiceModal.value = true;
        break;
      case 'refund':
        segment.track('register.sales.refund.clicked');
        showRefundSaleModal.value = true;
        break;
      case 'print':
        promptPrint.value = true;
        break;
      case 'delete':
        deleteInvoice();
        break;
      case 'note':
        showCreateNoteModal.value = true;
        break;
      case 'view-documents':
        scrollPage({ element: documentsRef.value?.$el });
        break;
      case 'send-payment-link':
        showSendPaymentLinkModal.value = true;
        break;
    }
  }
};

const deleteInvoice = () => {
  modal('confirmation', {
    message:
      !order.value?.draft && hasFeatureFlag('compliant-register')
        ? t('global.actions.void_sale_number', { number: order.value?.number })
        : '',
    subMessage:
      !order.value?.draft && hasFeatureFlag('compliant-register')
        ? t('register.sale.void_sale_message')
        : '',
    warning: order.value?.draft
      ? ''
      : hasFeatureFlag('compliant-register')
        ? t('register.sale.void_sale_warning')
        : t('register.delete_warning'),
    type: 'cancel',
    item: t('global.items.invoice'),
  }).then(() => {
    deleteOrderMutation().then(() => {
      flash(t('register.deleted'));
      router.push({ name: props.parentRouteName });
    });
  });
};

const setNote = (value: string) => {
  updateOrderMutation({
    input: {
      id: order.value?.id,
      note: value,
    },
  }).then(({ data: { updateOrder } }: any) => {
    if (!updateOrder.errors && order.value) {
      flash(t('global.flash.note_updated'));
      order.value = { ...order.value, comments: updateOrder.order.comments };
    }
  });
};
</script>
